import React, { useEffect, useMemo, useRef, useState } from "react";
import { Table, Input, Select, App as AntApp, Space, Typography, Button, InputNumber, Popconfirm, Tooltip } from "antd";
import { EditOutlined } from "@ant-design/icons";

/**
 * FrmInventarioTable
 * - Tabla paginada (20 ítems) con búsqueda en vivo
 * - Columnas: Producto (Nombre), Cantidad (calculada por unidad), U Medida (Select)
 * - Consume:
 *    GET {BASE_URL}/Producto/inventario?search=...&page=...&pageSize=20
 *    GET {BASE_URL}/UnidadMedida/tipo/{tipoMedidaId}/activos
 * - BASE_URL: process.env.REACT_APP_API_URL || "https://localhost:7118/api"
 */

const BASE_URL = process.env.REACT_APP_API_URL || "https://localhost:7118/api";
const PAGE_SIZE = 20;

export default function FrmInventarioTable() {
  const { message } = AntApp.useApp();
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Datos de productos con inventario
  const [rows, setRows] = useState([]);

  // Cache de unidades por tipoMedidaId
  const [unitsByTipo, setUnitsByTipo] = useState({}); // { [tipoMedidaId]: Unidad[] }
  const unitsById = useMemo(() => {
    const m = new Map();
    Object.values(unitsByTipo).forEach((arr) => {
      (arr || []).forEach((u) => m.set(Number(u.id), u));
    });
    return m;
  }, [unitsByTipo]);

  // Unidad seleccionada por productoId
  const [selectedUnitByProduct, setSelectedUnitByProduct] = useState({});

  // Debounce para búsqueda
  const debounceRef = useRef(null);
  const onSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    setPage(1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchData(1, val);
    }, 300);
  };

  // Helper para normalizar respuesta .NET
  const toArray = (maybe) => {
    if (Array.isArray(maybe)) return maybe;
    if (maybe && Array.isArray(maybe.$values)) return maybe.$values;
    return [];
  };

  // Fetch unidades por tipo (con cache)
  const fetchUnitsByTipo = async (tipoMedidaId) => {
    if (!tipoMedidaId) return [];
    if (unitsByTipo[tipoMedidaId]) return unitsByTipo[tipoMedidaId];
    try {
      const res = await fetch(`${BASE_URL}/UnidadMedida/tipo/${tipoMedidaId}/activos`);
      if (!res.ok) throw new Error("Error al cargar unidades por tipo");
      const data = await res.json();
      const list = toArray(data);
      setUnitsByTipo((prev) => ({ ...prev, [tipoMedidaId]: list }));
      return list;
    } catch (err) {
      console.error(err);
      message.error("No se pudieron cargar las unidades (tipo)");
      return [];
    }
  };

  // Cargar datos iniciales
  useEffect(() => {
    fetchData(page, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async (pageArg = 1, searchArg = "") => {
    try {
      setLoading(true);
      const url = new URL(`${BASE_URL}/Producto/inventario`);
      url.searchParams.set("search", searchArg || "");
      url.searchParams.set("page", String(pageArg));
      url.searchParams.set("pageSize", String(PAGE_SIZE));

      const res = await fetch(url.toString());
      if (!res.ok) throw new Error("Error al cargar datos");
      const data = await res.json();

      const rawItems = data.items ?? data.Items ?? data;
      const items = toArray(rawItems);
      const totalVal = data.total ?? data.Total ?? items.length;
      setTotal(totalVal);

      const mapped = items.map((x) => ({
        key: x.productoId ?? x.ProductoId,
        productoId: x.productoId ?? x.ProductoId,
        nombre: x.nombre ?? x.Nombre,
        cantidad: x.cantidad ?? x.Cantidad,
        tipoMedidaId: x.tipoMedidaId ?? x.TipoMedidaId,
        idUnidadMedida: x.idUnidadMedida ?? x.IdUnidadMedida,
      }));
      setRows(mapped);

      // Inicializar selección con idUnidadMedida si viene del producto
      setSelectedUnitByProduct((prev) => {
        const next = { ...prev };
        for (const r of mapped) {
          if (r.idUnidadMedida && next[r.productoId] == null) {
            next[r.productoId] = Number(r.idUnidadMedida);
          }
        }
        return next;
      });

      // Pre-cargar unidades por tipoMedidaId
      const tipos = Array.from(new Set(mapped.map((r) => r.tipoMedidaId).filter(Boolean)));
      tipos.forEach((t) => { void fetchUnitsByTipo(t); });

      setPage(pageArg);
    } catch (err) {
      console.error(err);
      message.error("No se pudieron cargar los productos");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
    fetchData(pageNumber, search);
  };

  const handleUnitChange = (productoId, unitId) => {
    setSelectedUnitByProduct((prev) => ({ ...prev, [productoId]: Number(unitId) }));
  };

  // ── Ajuste manual de stock ──
  const [ajusteValores, setAjusteValores] = useState({}); // { productoId: numero }
  const [ajustando, setAjustando] = useState({});         // { productoId: true/false }

  const onAjustarStock = async (productoId) => {
    const nuevaCantidad = ajusteValores[productoId];
    if (nuevaCantidad == null) { message.warning("Ingrese la nueva cantidad."); return; }
    try {
      setAjustando(prev => ({ ...prev, [productoId]: true }));
      const res = await fetch(`${BASE_URL}/Inventario/producto/${productoId}/cantidad`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cantidad: nuevaCantidad }),
      });
      if (!res.ok) throw new Error("Error al ajustar");
      message.success("Stock actualizado correctamente.");
      setAjusteValores(prev => { const n = { ...prev }; delete n[productoId]; return n; });
      fetchData(page, search);
    } catch {
      message.error("No se pudo actualizar el stock.");
    } finally {
      setAjustando(prev => ({ ...prev, [productoId]: false }));
    }
  };

  // Muestra la cantidad exactamente como está almacenada en la BD.
  //
  // NOTA TÉCNICA: El sistema guarda los valores en la unidad natural del producto
  // (ej: ml para betaferol) sin conversión a unidad base. La columna "U Medida"
  // es solo informativa (etiqueta). NO se divide por FactorBase porque todas las
  // operaciones (Ajustar, descuento en salud) también usan la unidad natural,
  // lo que garantiza consistencia: 500ml - 16ml = 484ml.
  const renderCantidad = (value) => {
    return Number(value ?? 0);
  };

  const columns = [
    {
      title: "Producto",
      dataIndex: "nombre",
      key: "nombre",
      ellipsis: true,
    },
    {
      title: "Cantidad",
      dataIndex: "cantidad",
      key: "cantidad",
      render: renderCantidad,
    },
    {
      title: "Ajustar stock",
      key: "ajuste",
      width: 260,
      render: (_, record) => (
        <Space>
          <InputNumber
            size="small"
            style={{ width: 110 }}
            placeholder="Nueva cantidad"
            value={ajusteValores[record.productoId] ?? null}
            onChange={val => setAjusteValores(prev => ({ ...prev, [record.productoId]: val }))}
            step={1}
          />
          <Tooltip title="Guardar nueva cantidad">
            <Popconfirm
              title={`¿Establecer stock de "${record.nombre}" en ${ajusteValores[record.productoId] ?? "—"} ${(unitsById.get(Number(selectedUnitByProduct[record.productoId] ?? record.idUnidadMedida)))?.abreviatura ?? ""}?`}
              okText="Sí, ajustar"
              cancelText="Cancelar"
              onConfirm={() => onAjustarStock(record.productoId)}
              disabled={ajusteValores[record.productoId] == null}
            >
              <Button
                size="small"
                type="primary"
                icon={<EditOutlined />}
                loading={ajustando[record.productoId]}
                disabled={ajusteValores[record.productoId] == null}
              >
                Ajustar
              </Button>
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: "U Medida",
      key: "uMedida",
      width: 260,
      render: (_, record) => {
        const rawUnitId = selectedUnitByProduct[record.productoId] ?? record.idUnidadMedida;
        const unitId = rawUnitId != null ? Number(rawUnitId) : undefined;
        const options = (unitsByTipo[record.tipoMedidaId] || []).map((u) => ({
          value: Number(u.id),
          label: `${u.nombre} (${u.abreviatura})`,
        }));
        return (
          <Select
            value={unitId}
            allowClear
            placeholder="Seleccione una unidad de medida"
            style={{ width: "100%" }}
            onChange={(val) => handleUnitChange(record.productoId, val)}
            options={options}
            showSearch
            optionFilterProp="label"
          />
        );
      },
    },
  ];

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      <Typography.Title level={4} style={{ margin: 0 }}>
        Inventario de Productos
      </Typography.Title>
      <Input
        allowClear
        placeholder="Buscar producto o código de barras"
        onChange={onSearchChange}
        size="large"
      />

      <Table
        bordered
        size="middle"
        loading={loading}
        columns={columns}
        dataSource={rows}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          showSizeChanger: false,
          onChange: handlePageChange,
        }}
        locale={{
          emptyText: search
            ? "Sin resultados para la búsqueda"
            : "Sin datos",
        }}
      />
    </Space>
  );
}